// contexts/PlatformContext.tsx
import React, { useEffect, useState, ReactNode } from 'react';
import { platformService } from '../services/platform';
import { hapticFeedback } from '../services/haptics';
import { notificationService } from '../services/notificationService';
import { analyticsService } from '../services/analytics';
import { initSentry } from '../instrument.js';
import type { Platform, PlatformCapabilities } from '../types/platform';
import type { AppType } from '../types';
import { PlatformContext, type PlatformContextValue } from './platformContextValue';

interface PlatformProviderProps {
    children: ReactNode;
    isTelegramContext: boolean;
}

/**
 * Wait for Telegram WebApp to be available
 */
const waitForTelegram = (shouldWait: boolean, timeout = 3000): Promise<boolean> => {
    return new Promise((resolve) => {
        // If already available, resolve immediately
        if (window.Telegram?.WebApp) {
            resolve(true);
            return;
        }

        // If we're not in Telegram context, don't wait
        if (!shouldWait) {
            resolve(false);
            return;
        }

        const startTime = Date.now();

        const checkTelegram = () => {
            if (window.Telegram?.WebApp) {
                resolve(true);
                return;
            }

            if (Date.now() - startTime > timeout) {
                console.warn('Telegram WebApp timeout');
                resolve(false);
                return;
            }

            requestAnimationFrame(checkTelegram);
        };

        checkTelegram();
    });
};

export const PlatformProvider: React.FC<PlatformProviderProps> = ({ children, isTelegramContext }) => {
    const [isInitialized, setIsInitialized] = useState(false);
    const [platform, setPlatform] = useState<Platform | null>(null);
    const [capabilities, setCapabilities] = useState<PlatformCapabilities | null>(null);
    const [appType, setAppType] = useState<AppType>('hallouminati');

    useEffect(() => {
        const initialize = async () => {
            try {
                // Wait for Telegram only if we're in Telegram context
                await waitForTelegram(isTelegramContext);

                // Now initialize platform service
                await platformService.initialize();

                // Detect platform and capabilities
                const detectedPlatform = platformService.detectPlatform();
                const detectedCapabilities = platformService.getCapabilities();
                const detectedAppType = await platformService.getAppType();

                setPlatform(detectedPlatform);
                setCapabilities(detectedCapabilities);
                setAppType(detectedAppType);
                setIsInitialized(true);

                // Initialize analytics with the detected appType
                analyticsService.initialize(detectedAppType);

                // No-op unless this is a fresh native install, where the bundle
                // id is the only signal for which Sentry project owns the app
                // and instrument.js could not resolve it synchronously at boot.
                initSentry(detectedAppType);

                console.log('Platform initialized:', {
                    platform: detectedPlatform,
                    appType: detectedAppType,
                    capabilities: detectedCapabilities,
                    isTelegramContext,
                });
            } catch (error) {
                console.error('Platform initialization error:', error);
                // Fallback to web platform
                setPlatform(platformService.detectPlatform());
                setCapabilities(platformService.getCapabilities());
                setIsInitialized(true);
            }
        };

        initialize();
    }, [isTelegramContext]);

    const telegram = platformService.getTelegramWebApp();

    // Don't render children until platform is initialized
    if (!isInitialized || !platform || !capabilities) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-ground-session">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-ink-muted">Initializing platform...</p>
                </div>
            </div>
        );
    }

    const value: PlatformContextValue = {
        platform,
        capabilities,
        isInitialized,
        isTelegramMiniApp: platformService.isTelegramMiniApp(),
        isNativeApp: platformService.isNativeApp(),
        isWeb: platformService.isWeb(),
        isIOS: platformService.isIOS(),
        isAndroid: platformService.isAndroid(),
        telegram,
        telegramUser: telegram?.initDataUnsafe?.user,
        startParam: telegram?.initDataUnsafe?.start_param,
        appType,
        hapticFeedback,
        checkPushPermission: notificationService.checkPermission.bind(notificationService),
        requestPushPermission: notificationService.requestPermission.bind(notificationService),
    };

    return <PlatformContext.Provider value={value}>{children}</PlatformContext.Provider>;
};
